import AmplifyK5Button from '@amplify/amplify-button';
import clsx from 'clsx';
import { MouseEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import LockIcon from 'src/assets/images/icons/lock-icon.png';
import { AssignmentFeedback } from 'src/components/assignments/assignment-feedback';
import { K5CardImage } from 'src/components/elementary-school-view/k5-card-image';
import { Tooltip } from 'src/components/tooltip';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useFeedback } from 'src/hooks/use-feedback.hook';
import { useFormatAssignmentDueDate } from 'src/hooks/use-format-assignment-due-date';
import { useScore } from 'src/hooks/use-score.hook';
import { useTrimmedElementCheck } from 'src/hooks/use-trimmed-element-check';
import {
  AssignmentsViewOptions,
  DesmosAssignment,
  MSAssignmentsStatusOptions,
  SingleAssignment,
} from 'src/interfaces/assignment.types';
import { TaughtLessonAssignment } from 'src/interfaces/class-stats.types';
import { Theme } from 'src/interfaces/theme.types';
import AuthService from 'src/services/api/auth.service';
import { useCurrentGradeSelector } from 'src/store/features';
import {
  getAssignmentLessonTitle,
  isDesmosAssignmentContent,
} from 'src/utils/assignments.utils';
import { getTheme } from 'src/utils/theme.utils';

import { Subtitle } from '../subtitle';

type HigherGradeCardContentProps = {
  assignment: SingleAssignment | TaughtLessonAssignment | DesmosAssignment;
  thumbnailPath?: string;
  isThumbnailDistorted?: boolean;
  isFullyCustom?: boolean;
  isCustomized?: boolean;
  domain?: string;
  title?: string;
  isTaughtLesson?: boolean;
  dueDate?: number | null;
  isOverdue?: boolean;
  isStatusChangeDialogVisible?: boolean;
  onUndoButtonClick?: (e: MouseEvent<HTMLElement>) => void;
  isCardRemovalAnimationInProgress?: boolean;
  section?: AssignmentsViewOptions.TODO | AssignmentsViewOptions.PAST_WORK;
};

export const HigherGradeCardContent = ({
  assignment,
  thumbnailPath,
  isThumbnailDistorted,
  isFullyCustom,
  isCustomized,
  domain,
  title,
  isTaughtLesson,
  dueDate,
  isOverdue,
  isStatusChangeDialogVisible,
  onUndoButtonClick,
  isCardRemovalAnimationInProgress,
  section,
}: HigherGradeCardContentProps) => {
  const { t } = useTranslation();

  const formatAssignmentDueDate = useFormatAssignmentDueDate();
  const currentGrade = useCurrentGradeSelector();
  const theme = getTheme(currentGrade, false);

  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const isPastWorkSectionActive = section === AssignmentsViewOptions.PAST_WORK;
  const isToDoSectionActive = section === AssignmentsViewOptions.TODO;
  const isStudent = AuthService.isStudent();

  const { content, isHandedInmCLASSAssessment } = assignment;
  const unitTitle = 'unitTitle' in content ? content.unitTitle : undefined;
  const subtitle = 'subtitle' in content ? content.subtitle : undefined;
  const lessonKey = 'lessonKey' in content ? content.lessonKey : undefined;

  const lessonKeyToSearchInsight = !isTaughtLesson ? lessonKey : undefined;

  const isSscAssignment = assignment.enrollmentType === 'single_session';

  const { feedback } = useFeedback(
    assignment.contentBk,
    lessonKeyToSearchInsight,
  );
  const { score } = useScore(assignment.contentBk, lessonKeyToSearchInsight);

  const isDesmos = isDesmosAssignmentContent(content);

  const assignmentNameRef = useRef<HTMLHeadingElement>(null);

  // there might element clientHeight difference in 1px during first run of useTrimmedElementCheck
  // after switching to mobile layout on devices with retina display
  const assignmentNameHeightTolerance = 1;
  const isAssignmentNameTrimmed = useTrimmedElementCheck(
    assignmentNameRef,
    assignmentNameHeightTolerance,
  );

  const assignmentNameLabel =
    isCustomized || isDesmos ? title : getAssignmentLessonTitle(title);

  return (
    <>
      <div className="assignment__thumbnail-wrapper">
        <K5CardImage
          theme={theme}
          imagePath={thumbnailPath}
          classNames={{
            containerClassnames: 'assignment__thumbnail-container',
            imageClassnames: clsx('assignment__thumbnail', {
              'assignment__thumbnail--distorted':
                thumbnailPath && isThumbnailDistorted,
            }),
          }}
          withCheckmark={
            isCardRemovalAnimationInProgress && isToDoSectionActive
          }
        />
        {isHandedInmCLASSAssessment && (
          <img
            className="assignment__thumbnail-wrapper__lock"
            src={LockIcon}
            alt="lock-icon"
          />
        )}
      </div>
      <div
        className={clsx(
          'assignment__info-container',
          `assignment__info-container--${theme}`,
          {
            'assignment__info-container--full-custom': isFullyCustom,
            'assignment__info-container--hidden-to-do':
              isStatusChangeDialogVisible && isToDoSectionActive,
            'assignment__info-container--reduced-past-work':
              isStatusChangeDialogVisible && isPastWorkSectionActive,
          },
        )}
      >
        <div
          className={clsx('assignment__header', {
            'assignment__header--fully-custom': isFullyCustom,
          })}
        >
          {!isFullyCustom && (
            <Subtitle
              assignment={assignment}
              subtitle={subtitle}
              assignmentDomain={domain}
              unitTitle={unitTitle}
              assignmentTitle={title}
              isStatusChangeDialogVisible={isStatusChangeDialogVisible}
            />
          )}
          {!isTaughtLesson && dueDate && (
            <>
              <div
                className={clsx('assignment__date-container', {
                  'assignment__date-container--hidden':
                    isStatusChangeDialogVisible,
                })}
              >
                <p className={`assignment__date assignment__date--${theme}`}>
                  {`${t('Due')}: ${formatAssignmentDueDate(new Date(dueDate))}`}
                </p>
              </div>
            </>
          )}
        </div>
        <div className="assignment__body">
          <Tooltip
            title={assignmentNameLabel ?? ''}
            classNames={{ tooltip: 'sh-mobile-tooltip-top-left' }}
            suppress={!isAssignmentNameTrimmed}
          >
            <h2
              ref={assignmentNameRef}
              className="assignment__name assignment__name--higher-grade"
              tabIndex={isAssignmentNameTrimmed ? 0 : -1}
            >
              {assignmentNameLabel}
            </h2>
          </Tooltip>
          <div
            className={clsx('assignment__action-buttons', {
              'assignment__action-buttons--hidden': isStatusChangeDialogVisible,
            })}
          >
            <div
              className={clsx(
                'assignment__feedback',
                `assignment__feedback--${theme}`,
                {
                  'assignment__feedback--hidden': isStatusChangeDialogVisible,
                },
              )}
            >
              <AssignmentFeedback
                feedback={feedback}
                score={score}
                assignment={assignment}
                commonXapiContextPayload={commonXapiContextPayload}
                assignmentStatus={MSAssignmentsStatusOptions.STARTED}
              />
            </div>
            {isStudent &&
              isPastWorkSectionActive &&
              !isTaughtLesson &&
              !isSscAssignment &&
              !isOverdue &&
              !isHandedInmCLASSAssessment && (
                <div
                  className={clsx(
                    'theme-k5 layout-context-delta assignment__undo-btn-container',
                    {
                      'assignment__undo-btn-container--hidden':
                        isStatusChangeDialogVisible,
                    },
                  )}
                >
                  <AmplifyK5Button
                    variant="secondary"
                    className={clsx(
                      'btn-icon',
                      'assignment__undo-btn',
                      `assignment__undo-btn--${theme}`,
                      {
                        'assignment__undo-btn--hidden':
                          isStatusChangeDialogVisible,
                        'btn-raised': theme !== Theme.FOURTH_FIFTH,
                      },
                    )}
                    icon="icon-k5-undo"
                    onClick={onUndoButtonClick}
                    tabIndex={isStatusChangeDialogVisible ? -1 : 0}
                  />
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
};
